import React from 'react';
import { Button, Dropdown, Input, Select, Space } from 'antd';
import type { MenuProps } from 'antd';
import {
  DiffOutlined,
  ImportOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoreOutlined,
  PlusOutlined,
  RocketOutlined,
  UploadOutlined,
} from '@ant-design/icons';

export type LibraryViewMode = 'browse' | 'catalogue' | 'package';

export interface EpcWbsLibraryCommandBarProps {
  mode: LibraryViewMode;
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;
  readinessFilter?: string;
  onReadinessFilterChange?: (v: string) => void;
  sortFilter?: string;
  onSortFilterChange?: (v: string) => void;
  onComparePackages?: () => void;
  canComparePackages?: boolean;
  readOnly?: boolean;
  railCollapsed?: boolean;
  onToggleRail?: () => void;
  onNewLibrary: () => void;
  onNewPackage?: () => void;
  onSeed: () => void;
  onImportPackage: () => void;
  onPromote: () => void;
  onOpenCbs: () => void;
  seeding?: boolean;
  hasLibrarySelected?: boolean;
}

export const EpcWbsLibraryCommandBar: React.FC<EpcWbsLibraryCommandBarProps> = ({
  mode,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  readinessFilter = 'all',
  onReadinessFilterChange,
  sortFilter = 'code',
  onSortFilterChange,
  onComparePackages,
  canComparePackages,
  readOnly,
  railCollapsed,
  onToggleRail,
  onNewLibrary,
  onNewPackage,
  onSeed,
  onImportPackage,
  onPromote,
  onOpenCbs,
  seeding,
  hasLibrarySelected,
}) => {
  const moreItems: MenuProps['items'] = [
    { key: 'cbs', icon: <UploadOutlined />, label: 'Company CBS' },
    { key: 'seed', icon: <RocketOutlined />, label: 'Bootstrap sample catalogue', disabled: seeding },
    { key: 'import', icon: <ImportOutlined />, label: 'Import package', disabled: !hasLibrarySelected },
    { key: 'promote', label: 'Promote from estimate' },
  ];

  const placeholder =
    mode === 'package'
      ? 'Filter WBS nodes…'
      : mode === 'catalogue'
        ? 'Search packages…'
        : 'Search catalogues…';

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderBottom: '1px solid #f0f0f0',
        background: '#fff',
      }}
    >
      <Space wrap>
        {onToggleRail && mode !== 'browse' ? (
          <Button
            type="text"
            icon={railCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onToggleRail}
            title={railCollapsed ? 'Show catalogues' : 'Hide catalogues'}
          />
        ) : null}
        <Input.Search
          allowClear
          placeholder={placeholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ width: mode === 'package' ? 220 : 260 }}
        />
        {mode !== 'package' ? (
          <Select
            value={statusFilter}
            onChange={onStatusFilterChange}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'Active', label: 'Active' },
              { value: 'Draft', label: 'Draft' },
            ]}
          />
        ) : null}
        {mode === 'catalogue' && onReadinessFilterChange ? (
          <Select
            value={readinessFilter}
            onChange={onReadinessFilterChange}
            style={{ width: 140 }}
            options={[
              { value: 'all', label: 'All readiness' },
              { value: 'ready', label: 'Plug-ready' },
              { value: 'incomplete', label: 'Needs work' },
            ]}
          />
        ) : null}
        {mode === 'catalogue' && onSortFilterChange ? (
          <Select
            value={sortFilter}
            onChange={onSortFilterChange}
            style={{ width: 130 }}
            options={[
              { value: 'code', label: 'Sort: code' },
              { value: 'name', label: 'Sort: name' },
              { value: 'usage', label: 'Sort: usage' },
              { value: 'nodes', label: 'Sort: nodes' },
            ]}
          />
        ) : null}
      </Space>

      <Space wrap>
        {mode === 'catalogue' && onComparePackages && canComparePackages ? (
          <Button icon={<DiffOutlined />} onClick={onComparePackages}>
            Compare
          </Button>
        ) : null}
        {!readOnly && mode === 'catalogue' ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onNewPackage}>
            New package
          </Button>
        ) : null}
        {!readOnly && mode === 'browse' ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onNewLibrary}>
            New catalogue
          </Button>
        ) : null}
        {!readOnly ? (
          <Dropdown
            menu={{
              items: moreItems,
              onClick: ({ key }) => {
                if (key === 'cbs') onOpenCbs();
                if (key === 'seed') onSeed();
                if (key === 'import') onImportPackage();
                if (key === 'promote') onPromote();
              },
            }}
          >
            <Button icon={<MoreOutlined />}>More</Button>
          </Dropdown>
        ) : (
          <Button icon={<UploadOutlined />} onClick={onOpenCbs}>
            Company CBS
          </Button>
        )}
      </Space>
    </div>
  );
};
